/**
 * `/liturgies/{id}/history` — screen 13.
 *
 * Six record types as tabs, entries showing previous and new value side by side, and one rule that
 * decides the shape of the whole page:
 *
 * > A Participant Capability never sees Administrative Audit. (11-screens.md)
 *
 * The server returns a withheld record type as **null rather than `[]`**, and this screen renders
 * that faithfully: the tab is **absent**, not present-and-empty. A greyed-out "Administrative" tab
 * would announce that governance actions exist and are being kept from you, which is precisely the
 * disclosure the 404-not-403 rule exists to prevent.
 *
 * The six stay separate rather than merging into one feed because they answer six different
 * questions. "Revision requested by Maria" and "revision-request email sent to m***@example.test"
 * describe one moment and are two different facts — the second can fail while the first stands
 * (L2-13 §48). A merged timeline would quietly imply the email arrived.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import { fetchLiturgy } from '../liturgies/api';
import { fetchHistory, RECORD_TYPES, summarize, type HistoryEntry, type RecordType } from './api';
import './history.css';

export function HistoryScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/history' });
  const { t } = useTranslation('common');
  const [tab, setTab] = useState<RecordType>('revision');

  const liturgy = useQuery({ queryKey: ['liturgy', id], queryFn: () => fetchLiturgy(id) });
  const history = useQuery({ queryKey: ['history', id], queryFn: () => fetchHistory(id) });

  const error = history.error instanceof Error ? (history.error as ApiError) : undefined;
  const data = history.data?.data;

  // Only the types this requester may see. Absent, not disabled — see the note above.
  const visible = RECORD_TYPES.filter((type) => data == null || data[type.value] !== null);

  // If authority is narrower than the default tab assumes, fall back to the first one they have
  // rather than showing a blank panel.
  const active = visible.some((type) => type.value === tab)
    ? tab
    : (visible[0]?.value ?? 'revision');
  const definition = RECORD_TYPES.find((type) => type.value === active);
  const entries = data?.[active] ?? [];

  return (
    <div className="hist">
      <header className="hist__head">
        <div>
          <h1 className="hist__title">{t('history.title')}</h1>
          <p className="hist__lede">
            {liturgy.data
              ? `Everything recorded about ${liturgy.data.title}, kept as six separate records.`
              : 'Everything recorded about this celebration, kept as six separate records.'}
          </p>
        </div>
        <Link className="hist__link" to="/liturgies/$id" params={{ id }}>
          {t('workspace.overview')} →
        </Link>
      </header>

      <WorkspaceTabs liturgyId={id} />

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <nav className="hist__tabs">
        {visible.map((type) => (
          <button
            key={type.value}
            type="button"
            className={`hist__tab${type.value === active ? ' hist__tab--on' : ''}`}
            onClick={() => setTab(type.value)}
          >
            {type.label}
          </button>
        ))}
      </nav>

      <div className="hist__grid">
        <div className="hist__main">
          <section className="hist__card">
            {/* The question this record type answers, stated before its entries. Six lists of
                timestamps look interchangeable without it. */}
            <p className="hist__question">{definition?.question}</p>
            <p className="hist__fine">{definition?.note}</p>

            {history.isPending ? (
              <p className="hist__fine">{t('history.loading')}</p>
            ) : entries.length === 0 ? (
              <p className="hist__empty">{t('history.empty')}</p>
            ) : (
              <ol className="hist__list">
                {entries.map((entry) => (
                  <Entry key={entry.id} entry={entry} type={active} />
                ))}
              </ol>
            )}
          </section>
        </div>

        <aside className="hist__side">
          <section className="hist__card hist__card--warm">
            <p className="hist__eyebrow">{t('history.why_six')}</p>
            <p className="hist__body">
              Each answers a different question. Merging them would lose the distinction that
              matters most: a decision and the email announcing it are two facts, and the email can
              fail while the decision stands.
            </p>
          </section>

          <section className="hist__card">
            <p className="hist__eyebrow">{t('history.attribution')}</p>
            <p className="hist__body">
              Somebody planning through a shared link is recorded as a link, not invented into a
              name. Where a name appears, an Account was signed in.
            </p>
            <p className="hist__fine">
              Access records never contain the link itself — only when one was issued, replaced or
              withdrawn.
            </p>
          </section>

          {/* Shown only where it is true. Saying "you are seeing everything" to somebody who is not
              would be worse than saying nothing. */}
          {data && visible.length < RECORD_TYPES.length ? (
            <section className="hist__card">
              <p className="hist__eyebrow">{t('history.what_you_see')}</p>
              <p className="hist__body">
                These records follow your authority over this celebration. Records that
                administering it would show are not listed here.
              </p>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function Entry({ entry, type }: { entry: HistoryEntry; type: RecordType }): React.JSX.Element {
  const { t } = useTranslation('common');
  const changed = entry.before != null && entry.after != null && entry.before !== entry.after;

  return (
    <li className="hist__entry">
      <div className="hist__when">{entry.at.slice(0, 16).replace('T', ' ')}</div>

      <div className="hist__what">
        <div className="hist__summary">{summarize(entry.summary, type, t)}</div>
        <div className="hist__actor">{entry.actor}</div>

        {/* Side by side, per the mockup. A diff read as two adjacent columns is legible; the same
            two values on consecutive lines are not. */}
        {changed ? (
          <div className="hist__diff">
            <div className="hist__diff-side hist__diff-side--before">
              <span className="hist__diff-label">{t('history.was')}</span>
              <span className="hist__diff-value">{entry.before}</span>
            </div>
            <div className="hist__diff-side hist__diff-side--after">
              <span className="hist__diff-label">{t('history.became')}</span>
              <span className="hist__diff-value">{entry.after}</span>
            </div>
          </div>
        ) : entry.after ? (
          <div className="hist__value">{entry.after}</div>
        ) : null}

        {entry.detail ? <div className="hist__detail">{entry.detail}</div> : null}
      </div>
    </li>
  );
}
